/**
 * Active-organization switcher for the sidebar. Shows the current context
 * (personal or an org) and lets the user switch. Switching runs the
 * set-active/clear-active → refresh → invalidate(me) flow in `useOrganizations`,
 * so the new context is live before the next read.
 *
 * Personal is actionable (clear-active) only when the user is currently in an org
 * AND has the personal-scope entitlement — org-only accounts cannot switch to a
 * context they may not use.
 *
 * Pending invitations are NOT shown here — they live in the prominent
 * InvitationsBanner (app shell) so they are not buried in a dropdown.
 */
import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Building2, Check, ChevronsUpDown, Plus, Settings2 } from 'lucide-react'
import { toast } from 'sonner'
import { useAuth } from '../lib/auth'
import { useOrganizations } from '../hooks/useOrganizations'
import { Button } from './ui/button'
import { CreateOrganizationDialog } from './CreateOrganizationDialog'
import { TESTID } from '../lib/testids'
import { ploteaApp } from '../lib/productProfile'

/**
 * The active-organization context and its switch actions, shared by the
 * sidebar switcher (Archigma) and the Site top bar's account menu (Plotea).
 */
export function useOrgSwitch(onDone?: () => void) {
  const { t } = useTranslation()
  const auth = useAuth()
  const { query, setActive, clearActive } = useOrganizations()

  const user = auth.status === 'authenticated' ? auth.user : null
  const activeOrg = user?.active_org ?? null
  const personalAllowed = user?.personal_scope ?? true
  const canCreate = user?.can_create_organization ?? false
  const orgs = query.data ?? []
  // The active-org claim carries only id/slug/role — resolve the display name
  // from the orgs list (fall back to slug until it loads).
  const activeOrgName = orgs.find((org) => org.id === activeOrg?.id)?.name
  const label = activeOrg
    ? (activeOrgName ?? activeOrg.slug)
    : t('organizations.personal')
  const busy = setActive.isPending || clearActive.isPending

  // Belonging to any organization revokes the personal context — a member has no
  // personal silo to switch to. Personal is only available with zero memberships.
  const personalAvailable = personalAllowed && orgs.length === 0
  // Personal is a real target only when leaving an org and personal is available.
  const canSwitchToPersonal = Boolean(activeOrg) && personalAvailable

  // Anything to switch TO other than the current context: personal (when in an
  // org) plus any org we're not already in. With no target — and no admin manage
  // action — the switcher offers nothing, so it renders as a static label rather
  // than a dropdown that only contains the already-selected item.
  const orgTargets = orgs.filter((org) => org.id !== activeOrg?.id)
  const canManage = orgs.length > 0 || canCreate
  const interactive = canSwitchToPersonal || orgTargets.length > 0 || canManage

  const switchTo = (id: string) => {
    onDone?.()
    setActive.mutate(id, {
      onSuccess: () => toast.success(t('organizations.toast.switched')),
      onError: () => toast.error(t('organizations.toast.error')),
    })
  }

  const switchToPersonal = () => {
    onDone?.()
    if (!canSwitchToPersonal) return
    clearActive.mutate(undefined, {
      onSuccess: () => toast.success(t('organizations.toast.switched')),
      onError: () => toast.error(t('organizations.toast.error')),
    })
  }
  return {
    activeOrg,
    orgs,
    label,
    busy,
    canCreate,
    canManage,
    interactive,
    personalAvailable,
    canSwitchToPersonal,
    switchTo,
    switchToPersonal,
    /** The organization page of this product. */
    managePath: ploteaApp
      ? ('/organization' as const)
      : ('/organizations' as const),
  }
}

export function OrgSwitcher() {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [showCreate, setShowCreate] = useState(false)
  const {
    activeOrg,
    orgs,
    label,
    busy,
    canCreate,
    canManage,
    interactive,
    personalAvailable,
    canSwitchToPersonal,
    switchTo,
    switchToPersonal,
    managePath,
  } = useOrgSwitch(() => setOpen(false))

  // Nothing to switch to → static display, not a select.
  if (!interactive) {
    return (
      <div className="px-2 py-2" data-testid={TESTID.orgSwitcher}>
        <div
          className="flex items-center gap-2 px-3 py-2 text-sm text-foreground"
          data-testid={TESTID.orgSwitcherCurrent}
        >
          <Building2 className="size-4 shrink-0 text-muted-foreground" />
          <span className="truncate">{label}</span>
        </div>
      </div>
    )
  }

  return (
    <div className="relative px-2 py-2" data-testid={TESTID.orgSwitcher}>
      <Button
        variant="outline"
        className="w-full justify-between gap-2"
        data-testid={TESTID.orgSwitcherTrigger}
        disabled={busy}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="flex items-center gap-2 truncate">
          <Building2 className="size-4 shrink-0" />
          <span className="truncate">{label}</span>
        </span>
        <ChevronsUpDown className="size-4 text-muted-foreground shrink-0" />
      </Button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute left-2 right-2 top-full z-50 mt-1 rounded-lg border border-border bg-card py-1 shadow-lg">
            {personalAvailable && (
              <button
                type="button"
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-foreground hover:bg-accent disabled:cursor-default disabled:text-muted-foreground disabled:hover:bg-transparent"
                data-testid={TESTID.orgSwitcherPersonal}
                disabled={!canSwitchToPersonal}
                onClick={switchToPersonal}
              >
                <Check
                  className={`size-4 ${activeOrg ? 'opacity-0' : 'opacity-100'}`}
                />
                {t('organizations.personal')}
              </button>
            )}
            {orgs.map((org) => (
              <button
                key={org.id}
                type="button"
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-foreground hover:bg-accent"
                data-testid={TESTID.orgSwitcherOption(org.id)}
                onClick={() => switchTo(org.id)}
              >
                <Check
                  className={`size-4 ${activeOrg?.id === org.id ? 'opacity-100' : 'opacity-0'}`}
                />
                <span className="truncate">{org.name}</span>
              </button>
            ))}
            {canManage && (
              <>
                <div className="my-1 border-t border-border" />
                {canCreate && (
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-foreground hover:bg-accent"
                    data-testid={TESTID.orgSwitcherCreate}
                    onClick={() => {
                      setOpen(false)
                      setShowCreate(true)
                    }}
                  >
                    <Plus className="size-4" />
                    {t('organizations.create.button')}
                  </button>
                )}
                <Link
                  to={managePath}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground"
                  data-testid={TESTID.orgSwitcherManage}
                  onClick={() => setOpen(false)}
                >
                  <Settings2 className="size-4" />
                  {t('organizations.switcher.manage')}
                </Link>
              </>
            )}
          </div>
        </>
      )}
      {canCreate && (
        <CreateOrganizationDialog
          open={showCreate}
          onOpenChange={setShowCreate}
        />
      )}
    </div>
  )
}
